import React, { useState, useEffect, useMemo } from "react";
import {
  Modal,
  Button,
  Form,
  Table,
  InputGroup,
  FormControl,
  Spinner,
  Badge,
  ListGroup,
  OverlayTrigger,
  Tooltip,
  Dropdown,
  DropdownButton,
  ButtonGroup,
} from "react-bootstrap";
import { Avatar } from "@mui/joy";
import {
  getEventsAPI,
  createEventAPI,
  setActiveEventAPI,
  getGroupDetailsAPI,
  addUserToGroupAPI,
  grantAwardToGroupAPI,
  searchUsersAPI,
  getAwardsAPI,
  updateGroupAPI,
} from "../reduxData/user/userAction";
import { toast } from "react-toastify";

const EventManagement = () => {
  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);

  const fetchEvents = async () => {
    setIsLoading(true);
    const data = await getEventsAPI();
    setEvents(data || []);
    setIsLoading(false);
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  const handleSetActive = async (eventId) => {
    const success = await setActiveEventAPI(eventId);
    if (success) fetchEvents();
  };

  const handleManageGroup = async (group) => {
    const groupDetails = await getGroupDetailsAPI(group._id);
    setSelectedGroup(groupDetails);
  };

  if (isLoading) {
    return (
      <div className="text-center p-5">
        <Spinner animation="border" variant="warning" />
      </div>
    );
  }

  if (selectedGroup) {
    return (
      <GroupDetailView
        group={selectedGroup}
        onBack={() => setSelectedGroup(null)}
        refreshGroup={handleManageGroup}
      />
    );
  }

  return (
    <div className="container-fluid">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3 className="fw-bold mb-0">Event Management</h3>
        <Button variant="warning" onClick={() => setShowCreateModal(true)}>
          <i className="fas fa-plus me-2"></i>Create Event
        </Button>
      </div>

      <div className="card shadow-sm border-0">
        <div className="card-body">
          <Table striped hover responsive>
            <thead className="table-light">
              <tr>
                <th>Event Name</th>
                <th>Status</th>
                <th>Associated Group</th>
                <th>Members</th>
                <th>Automatic Award</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {events.map((event) => (
                <tr key={event._id}>
                  <td className="fw-medium">{event.name}</td>
                  <td>
                    {event.isActive ? (
                      <Badge bg="success">Active</Badge>
                    ) : (
                      <Badge bg="secondary">Inactive</Badge>
                    )}
                  </td>
                  <td>{event.associatedGroup?.name}</td>
                  <td>{event.associatedGroup?.members?.length || 0}</td>
                  <td>
                    {event.associatedGroup?.associatedAward ? (
                      <Badge
                        pill
                        bg="info"
                        className="d-flex align-items-center"
                      >
                        <i className="fas fa-award me-1"></i>
                        {event.associatedGroup.associatedAward.text}
                      </Badge>
                    ) : (
                      <Badge pill bg="light" text="dark">
                        None
                      </Badge>
                    )}
                  </td>
                  <td className="text-end">
                    {!event.isActive && (
                      <Button
                        variant="outline-success"
                        size="sm"
                        className="me-2"
                        onClick={() => handleSetActive(event._id)}
                      >
                        Set Active
                      </Button>
                    )}
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => handleManageGroup(event.associatedGroup)}
                    >
                      Manage Group
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </div>

      <CreateEventModal
        show={showCreateModal}
        handleClose={() => setShowCreateModal(false)}
        onSuccess={fetchEvents}
      />
    </div>
  );
};

const GroupDetailView = ({ group, onBack, refreshGroup }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [showGrantAwardModal, setShowGrantAwardModal] = useState(false);
  const [showEditGroupModal, setShowEditGroupModal] = useState(false);

  const filteredMembers = useMemo(() => {
    if (!searchTerm) return group.members;

    const lowercasedFilter = searchTerm.toLowerCase();

    return group.members.filter((member) => {
      const hasUsername = member.user_name
        ?.toLowerCase()
        .includes(lowercasedFilter);
      const hasName = member.name?.toLowerCase().includes(lowercasedFilter);
      const hasEmail = member.email?.toLowerCase().includes(lowercasedFilter);

      return hasUsername || hasName || hasEmail;
    });
  }, [searchTerm, group.members]);
 
  const handleCopy = (fieldKey, fieldName) => {
    if (!group.members || group.members.length === 0) {
      toast.info("There are no members in this group to copy.");
      return;
    }

    const dataToCopy = group.members
      .map((member) => member[fieldKey])
      .filter(Boolean)
      .join("\n");

    navigator.clipboard.writeText(dataToCopy);
    toast.success(`${fieldName} copied to clipboard!`);
  };

  const onActionSuccess = () => {
    refreshGroup(group);
  };

  return (
    <div className="container-fluid">
      <Button variant="light" onClick={onBack} className="mb-3">
        <i className="fas fa-arrow-left me-2"></i>Back to Events
      </Button>
      <div className="card shadow-sm border-0">
        <div className="card-header bg-white p-3 d-flex justify-content-between align-items-center">
          <div>
            <h3 className="mb-0">{group.name}</h3>
            {group.associatedAward && (
              <p className="mb-0 text-muted">
                Automatically grants the "{group.associatedAward.text}" award.
              </p>
            )}
          </div>
          <div>
            <DropdownButton
              as={ButtonGroup}
              title="Copy Data"
              id="copy-data-dropdown"
              variant="secondary"
              className="me-2"
            >
              <Dropdown.Item
                eventKey="1"
                onClick={() => handleCopy("user_name", "Usernames")}
              >
                Copy Usernames
              </Dropdown.Item>
              <Dropdown.Item
                eventKey="2"
                onClick={() => handleCopy("name", "Names")}
              >
                Copy Full Names
              </Dropdown.Item>
              <Dropdown.Item
                eventKey="3"
                onClick={() => handleCopy("email", "Emails")}
              >
                Copy Emails
              </Dropdown.Item>
            </DropdownButton>
            <Button
              variant="success"
              size="sm"
              className="me-2"
              onClick={() => setShowAddUserModal(true)}
            >
              Add User
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setShowEditGroupModal(true)}
            >
              Edit
            </Button>
          </div>
        </div>
        <div className="card-body">
          <InputGroup className="mb-3">
            <FormControl
              placeholder="Search by username, name, or email..."
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </InputGroup>
          <Table striped bordered hover responsive>
            <thead>
              <tr>
                <th>Avatar</th>
                <th>Username</th>
                <th>Name</th>
                <th>Email</th>
              </tr>
            </thead>
            <tbody>
              {filteredMembers.map((member) => (
                <tr key={member._id}>
                  <td>
                    <Avatar src={member.profile_pic} size="sm" />
                  </td>
                  <td>{member.user_name}</td>
                  <td>{member.name}</td>
                  <td>{member.email}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </div>

      <AddUserToGroupModal
        show={showAddUserModal}
        handleClose={() => setShowAddUserModal(false)}
        group={group}
        onSuccess={() => refreshGroup(group)}
      />
      <EditGroupModal
        show={showEditGroupModal}
        handleClose={() => setShowEditGroupModal(false)}
        group={group}
        onSuccess={() => refreshGroup(group)}
      />
    </div>
  );
};

const CreateEventModal = ({ show, handleClose, onSuccess }) => {
  const [eventName, setEventName] = useState("");
  const [awards, setAwards] = useState([]);
  const [selectedAward, setSelectedAward] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (show) {
      const fetchAwards = async () => {
        const awardsData = await getAwardsAPI();
        setAwards(awardsData || []);
      };
      fetchAwards();
    }
  }, [show]);

  const handleSubmit = async () => {
    if (!eventName) {
      toast.error("Event name cannot be empty.");
      return;
    }
    setIsSubmitting(true);
    const success = await createEventAPI({
      name: eventName,
      associatedAwardId: selectedAward,
    });
    if (success) {
      onSuccess();
      handleClose();
      setEventName("");
      setSelectedAward("");
    }
    setIsSubmitting(false);
  };

  return (
    <Modal show={show} onHide={handleClose}>
      <Modal.Header closeButton>
        <Modal.Title>Create New Event</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-3">
          <Form.Label>Event Name</Form.Label>
          <Form.Control
            type="text"
            value={eventName}
            onChange={(e) => setEventName(e.target.value)}
            placeholder="e.g., Comic Con 2025"
          />
        </Form.Group>
        <Form.Group>
          <Form.Label>Automatic Award (Optional)</Form.Label>
          <Form.Select
            value={selectedAward}
            onChange={(e) => setSelectedAward(e.target.value)}
          >
            <option value="">Select an award to grant automatically...</option>
            {awards.map((award) => (
              <option key={award._id} value={award._id}>
                {award.text}
              </option>
            ))}
          </Form.Select>
          <Form.Text className="text-muted">
            This award will be given to every user who joins this event's group.
          </Form.Text>
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={isSubmitting}
        >
          {isSubmitting ? <Spinner as="span" size="sm" /> : "Create"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const EditGroupModal = ({ show, handleClose, group, onSuccess }) => {
  const [awards, setAwards] = useState([]);
  const [selectedAward, setSelectedAward] = useState(
    group.associatedAward?._id || ""
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (show) {
      const fetchAwards = async () => {
        const awardsData = await getAwardsAPI();
        setAwards(awardsData || []);
      };
      fetchAwards();
    }
  }, [show]);

  const handleSave = async () => {
    setIsSubmitting(true);
    const success = await updateGroupAPI(group._id, {
      associatedAward: selectedAward,
    });
    if (success) {
      onSuccess();
      handleClose();
    }
    setIsSubmitting(false);
  };

  return (
    <Modal show={show} onHide={handleClose}>
      <Modal.Header closeButton>
        <Modal.Title>Edit Group: {group.name}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Label>Automatic Award</Form.Label>
        <Form.Select
          value={selectedAward}
          onChange={(e) => setSelectedAward(e.target.value)}
        >
          <option value="">None</option>
          {awards.map((award) => (
            <option key={award._id} value={award._id}>
              {award.text}
            </option>
          ))}
        </Form.Select>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={isSubmitting}>
          Save Changes
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const AddUserToGroupModal = ({ show, handleClose, group, onSuccess }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [results, setResults] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (searchTerm.length > 2) {
      const delay = setTimeout(async () => {
        const users = await searchUsersAPI(searchTerm);
        setResults(users);
      }, 500);
      return () => clearTimeout(delay);
    } else {
      setResults([]);
    }
  }, [searchTerm]);

  const handleAddUser = async () => {
    if (!selectedUser) return;
    setIsSubmitting(true);
    const success = await addUserToGroupAPI(group._id, selectedUser._id);
    if (success) {
      onSuccess();
      handleClose();
    }
    setIsSubmitting(false);
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      onExited={() => {
        setSearchTerm("");
        setResults([]);
        setSelectedUser(null);
      }}
    >
      <Modal.Header closeButton>
        <Modal.Title>Add User to {group.name}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <FormControl
          placeholder="Search for user..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <ListGroup className="mt-3">
          {results.map((user) => (
            <ListGroup.Item
              action
              active={selectedUser?._id === user._id}
              key={user._id}
              onClick={() => setSelectedUser(user)}
            >
              {user.user_name}
            </ListGroup.Item>
          ))}
        </ListGroup>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleAddUser}
          disabled={!selectedUser || isSubmitting}
        >
          {isSubmitting ? "Adding..." : "Add User"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const GrantAwardToGroupModal = ({ show, handleClose, group, onSuccess }) => {
  const [awards, setAwards] = useState([]);
  const [selectedAward, setSelectedAward] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (show) {
      const fetchAwards = async () => {
        const awardsData = await getAwardsAPI();
        setAwards(awardsData);
      };
      fetchAwards();
    }
  }, [show]);

  const handleGrant = async () => {
    if (!selectedAward) return;
    setIsSubmitting(true);
    const success = await grantAwardToGroupAPI(group._id, selectedAward);
    if (success) {
      onSuccess();
      handleClose();
    }
    setIsSubmitting(false);
  };

  return (
    <Modal show={show} onHide={handleClose}>
      <Modal.Header closeButton>
        <Modal.Title>Grant Award to all in {group.name}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Select
          value={selectedAward}
          onChange={(e) => setSelectedAward(e.target.value)}
        >
          <option value="">Select an award...</option>
          {awards.map((award) => (
            <option key={award._id} value={award._id}>
              {award.text}
            </option>
          ))}
        </Form.Select>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button
          variant="warning"
          onClick={handleGrant}
          disabled={!selectedAward || isSubmitting}
        >
          {isSubmitting ? "Granting..." : "Grant Award to All"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EventManagement;
